import { network } from "web-utils-network";
import localConfig from "./localConfig";
import baseConfig from "./baseConfig";
import * as Sentry from "@sentry/browser";

let appConfig = null;
const CONFIG_RETRY_PERIOD = 2000;

function tryGettingConfig(resolve) {
  if (appConfig) {
    resolve(appConfig);
  } else {
    setTimeout(() => tryGettingConfig(resolve), CONFIG_RETRY_PERIOD);
  }
}

/**
 * Takes the baseObject and overrides the values present in objectOverrides at any level in the object's data structure.
 * The function ensures that baseObject doesn't lose any property and only the values present in objectOverrides are
 * updated in baseObject. This function can be resource intensive so it should be used carefully.
 * @param {Object} baseObject  An object containing a set of properties arbitrary levels deep.
 * @param {Object} objectOverrides An object having the same data structure as objectOverrides that will be used to
 * override the equivalent values present in baseObject.
 * @returns {Object}  An object based on baseObject but having the modifications described by objectOverrides
 */
// FIXME this function is repeated in WVL (called overrideDefaultTheme in StyleProvider.js), move to a common dependency
function deepMerge(baseObject, objectOverrides) {
  for (let property in objectOverrides) {
    if (objectOverrides.hasOwnProperty(property)) {
      if (objectOverrides[property] !== null && typeof objectOverrides[property] === "object") {
        baseObject[property] = baseObject[property] || {};
        deepMerge(baseObject[property], objectOverrides[property]);
      } else {
        baseObject[property] = objectOverrides[property];
      }
    }
  }
  return baseObject;
}

export const config = {
  /**
   * If there is a local config file already set, it just returns it synchronously. If there is no config file set yet
   * it will perform a network request to the remote server to gather one and return it asynchronously. If the network
   * call fails the base config file will be returned as a fallback by the promise.
   * @returns {Object|Promise<Object>}  A configuration object if there is already one set in memory or a promise that
   * will resolve to a configuration object once it is received from the remote location.
   */
  async request() {
    if (appConfig) {
      return appConfig;
    }
    try {
      const response = await network.get(`${localConfig.configUrl}/${localConfig.appName}`);
      appConfig = deepMerge(baseConfig, response.data);
      return appConfig;
    } catch (e) {
      appConfig = baseConfig;
      Sentry.captureException(e);
      return appConfig;
    }
  },
  /**
   * It returns the application configuration object if there is one in memory. In case of not having one, it will wait
   * for an amount of seconds to retry to get the object from memory. This method should be used as a getter and always
   * after the request method has been called before, given that this method only checks the local configuration file
   * periodically.
   * @returns {Object|Promise} The app config if it's ready in memory, or a Promise that will resolve when the config
   * file arrives to the app.
   */
  get() {
    return appConfig || new Promise(resolve => tryGettingConfig(resolve));
  }
};
